import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { AspectImage } from './AspectImage';
import { BtwHeading } from './BtwHeading';
import { FullscreenModal } from './FullscreenModal';
import { Pin } from './Pin';
import { ScrollArea as ScrollAreaPrimitive } from './ScrollArea';
import { Stack } from './Stack';
import { StepType } from './StepType';
import { cn } from './utils';
/**
 * Renders the root container for a step box
 * @param props StackProps containing width and children
 * @returns JSX element for the step box root container
 */
function Root(props) {
    const { className } = props, rest = __rest(props, ["className"]);
    return (_jsx(Stack, Object.assign({ width: "full", className: cn('max-w-3xl', className) }, rest)));
}
/**
 * Renders the actions section of a step box
 * @param props ActionsProps containing step type and children
 * @returns JSX element for the step box actions
 */
function Actions(props) {
    const { children, stepType } = props;
    return (_jsxs(Stack, { align: "center", className: "p-2", direction: "row", justify: "between", width: "full", children: [_jsx(StepType, { grayscale: true, size: "13", type: stepType }), _jsx(Stack, { className: "min-h-7", direction: "row", gap: "1", children: children })] }));
}
/**
 * Renders the body section of a step box
 * @param props React.HTMLAttributes containing className and rest
 * @returns JSX element for the step box body
 */
function Body(props) {
    const { className, isActive } = props, rest = __rest(props, ["className", "isActive"]);
    return (_jsx(Stack, Object.assign({ className: cn('ring-1', 'bg-white', 'rounded-2xl', 'ring-gray-600/20', 'shadow-sm-longer', {
            'ring-2 ring-gray-600': isActive,
        }), width: "full" }, rest)));
}
/**
 * Renders the header section of a step box
 * @param props HeaderProps containing title, image source, and children
 * @returns JSX element for the step box header
 */
function Header(props) {
    const { children, imageSrc, title, 'data-testid': testId } = props;
    return (_jsxs(Stack, { align: "center", className: "p-9", "data-testid": testId, direction: "row", gap: "8", width: "full", children: [_jsxs(Stack, { className: "gap-[14px]", width: "full", children: [_jsx(BtwHeading, { size: "base", children: title }), children] }), imageSrc && (_jsx(FullscreenModal, { trigger: _jsx(AspectImage, { alt: title, size: "16", src: imageSrc, srcSet: `${imageSrc} 2x` }), children: _jsx(FullscreenModal.Image, { "data-testid": `upload--${testId}-image-artwork`, alt: title, height: "contain", src: imageSrc }) }))] }));
}
/**
 * Renders the post header section of a step box
 * @param props StackProps containing children
 * @returns JSX element for the step box post header
 */
function PostHeader(props) {
    return _jsx(Stack, Object.assign({ className: "px-9 pb-9", gap: "9", width: "full" }, props));
}
/**
 * Renders the progress body section of a step box
 * @param props StackProps containing children
 * @returns JSX element for the step box progress body
 */
function ProgressBody(props) {
    return _jsx(Stack, Object.assign({ gap: "7", className: "px-9 pb-9", width: "full" }, props));
}
/**
 * Renders the scroll area section of a step box
 * @param props ScrollAreaProps containing children, className, and max height
 * @returns JSX element for the step box scroll area
 */
function ScrollArea(props) {
    const { children, className, maxHeght = 320 } = props;
    const ref = useRef(null);
    const { ref: scrollRef, inView } = useInView();
    const [size, setSize] = useState({
        width: undefined,
        height: maxHeght,
    });
    const onResize = useDebounceCallback(setSize, 200);
    useResizeObserver({
        ref,
        onResize,
    });
    return (_jsx(ScrollAreaPrimitive, { className: cn('px-7', className), style: {
            height: size && size.height && size.height > maxHeght ? maxHeght : 'auto',
        }, children: _jsxs("div", { ref: ref, children: [_jsx("div", { className: "h-px w-full", ref: scrollRef }), children, _jsx(Pin.Tl, { className: cn('w-full', 'h-px', 'bg-gray-600/10') }), _jsx(Pin.Tl, { className: cn('h-3', 'w-full', 'bg-gradient-to-b', 'from-gray-600/10', 'to-transparent', 'pointer-events-none', 
                    // Transitions
                    'duration-300', 'ease-smooth', 'transition-combined', 'will-change-transform', {
                        'opacity-0': inView,
                        'opacity-100': !inView,
                    }) })] }) }));
}
export const StepBox = {
    Actions,
    Body,
    Header,
    PostHeader,
    ProgressBody,
    Root,
    ScrollArea,
};
